import type { ScenarioEditPresenceRecord } from '../../data/types';

export type ScenarioAutoClaimWindow = {
  scenarioName: string;
  expiresAt: number;
} | null;

type AutoClaimArgs = {
  nextPresence: ScenarioEditPresenceRecord | null;
  previousPresence: ScenarioEditPresenceRecord | null;
  backendReachable: boolean;
  claimWindow: ScenarioAutoClaimWindow;
  nowMs: number;
};

type RecoverLockArgs = {
  nextPresence: ScenarioEditPresenceRecord | null;
  previousPresence: ScenarioEditPresenceRecord | null;
  backendReachable: boolean;
};

type ShowBannerArgs = {
  presence: ScenarioEditPresenceRecord | null;
};

type RefreshPresenceArgs = {
  nextPresence: ScenarioEditPresenceRecord | null;
  currentVersion: number | null;
};

type SoloConflictHealArgs = {
  presence: ScenarioEditPresenceRecord | null;
  backendReachable: boolean;
  scenarioName: string;
  syncState: {
    status: string;
    conflictCount: number;
    conflictScenarioName: string | null;
  };
};

export function getPresencePollIntervalMs(presence: ScenarioEditPresenceRecord | null): number {
  if (!presence) {
    return 5_000;
  }

  if (
    presence.collaborationActive ||
    presence.releasePending ||
    presence.releasePendingForCurrentUser ||
    presence.requestPendingForCurrentUser ||
    presence.requestDeniedForCurrentUser ||
    presence.canApproveRequest ||
    presence.canRequestAccess ||
    presence.canTakeOver ||
    !presence.lockActive
  ) {
    return 5_000;
  }

  return presence.isEditor ? 15_000 : 10_000;
}

export function shouldAutoClaimScenario({
  nextPresence,
  previousPresence,
  backendReachable,
  claimWindow,
  nowMs,
}: AutoClaimArgs): boolean {
  if (!nextPresence || !backendReachable) {
    return false;
  }

  const shouldClaimAsLastRemainingViewer = Boolean(
    previousPresence &&
    previousPresence.scenarioName === nextPresence.scenarioName &&
    previousPresence.collaborationActive &&
    !previousPresence.isEditor &&
    !nextPresence.lockActive &&
    nextPresence.otherViewerCount === 0 &&
    !nextPresence.requestPendingForCurrentUser &&
    !nextPresence.requestDeniedForCurrentUser,
  );

  if (shouldClaimAsLastRemainingViewer) {
    return true;
  }

  if (!claimWindow || claimWindow.scenarioName !== nextPresence.scenarioName) {
    return false;
  }

  if (nowMs > claimWindow.expiresAt) {
    return false;
  }

  return (
    !nextPresence.lockActive &&
    !nextPresence.isEditor &&
    !nextPresence.requestPendingForCurrentUser &&
    !nextPresence.requestDeniedForCurrentUser &&
    nextPresence.otherViewerCount === 0
  );
}

export function shouldRecoverEditorLock({
  nextPresence,
  previousPresence,
  backendReachable,
}: RecoverLockArgs): boolean {
  if (!nextPresence || !backendReachable) {
    return false;
  }

  return Boolean(
    previousPresence &&
    previousPresence.scenarioName === nextPresence.scenarioName &&
    previousPresence.isEditor &&
    !previousPresence.releasePending &&
    !nextPresence.lockActive &&
    !nextPresence.requestPendingForCurrentUser &&
    !nextPresence.requestDeniedForCurrentUser,
  );
}

export function canMutateCurrentScenarioOnline(presence: ScenarioEditPresenceRecord | null): boolean {
  if (!presence) {
    return false;
  }

  return presence.collaborationActive ? presence.isEditor : presence.canEdit;
}

export function shouldShowScenarioPresenceBanner({
  presence,
}: ShowBannerArgs): boolean {
  if (!presence) {
    return false;
  }

  return Boolean(
    presence.collaborationActive ||
    presence.requestPendingForCurrentUser ||
    presence.requestDeniedForCurrentUser ||
    presence.releasePending ||
    presence.releasePendingForCurrentUser ||
    presence.canApproveRequest ||
    presence.canTakeOver ||
    presence.canRequestAccess,
  );
}

export function shouldRefreshScenarioForPresence({
  nextPresence,
  currentVersion,
}: RefreshPresenceArgs): boolean {
  if (!nextPresence) {
    return false;
  }

  return Boolean(
    !nextPresence.isEditor &&
    nextPresence.collaborationActive &&
    nextPresence.currentVersion !== currentVersion,
  );
}

export function shouldAutoHealSoloEditorConflict({
  presence,
  backendReachable,
  scenarioName,
  syncState,
}: SoloConflictHealArgs): boolean {
  if (!presence || !backendReachable || !scenarioName) {
    return false;
  }

  return Boolean(
    syncState.status === 'conflict' &&
    syncState.conflictCount > 0 &&
    syncState.conflictScenarioName === scenarioName &&
    presence.scenarioName === scenarioName &&
    presence.isEditor &&
    !presence.releasePending &&
    presence.otherViewerCount === 0,
  );
}
